// BFF: Authenticated fetch using session cookie only (no API token in browser).
// All requests send credentials so the session cookie is attached.
// When the server returns 401, setAuthFailureCallback is invoked to redirect to login.

let _onAuthFailure = null;
let _authFailureFired = false;
let _signingOut = false;

// While the user is signing out, a background request's 401 must not bounce them to the login screen.
export function setSigningOut(value) {
  _signingOut = value;
}

/**
 * Register a callback when the session is effectively expired (server returns 401).
 * Use this to redirect the user to login (e.g. loginWithRedirect()).
 * Pass null to clear.
 */
export function setAuthFailureCallback(fn) {
  _onAuthFailure = fn;
  _authFailureFired = false; // Reset so each 401 can trigger redirect (e.g. after session expires)
}

// The wording browsers use when a request got no answer at all (server restarting, connection dropped).
const CONNECTION_ERROR_RE = /failed to fetch|networkerror|network error|load failed/i;

// What to show for a failed request: never the browser's own "Failed to fetch" text.
export function errorText(err, fallback) {
  if (CONNECTION_ERROR_RE.test(err?.message || "")) {
    return "We couldn't reach scoutm8 just now. Please check your connection and try again.";
  }
  return err?.message || fallback;
}

/**
 * Drop-in replacement for fetch() that sends the session cookie (credentials: 'include').
 * No token is sent; the backend uses the session set by the BFF auth flow (/api/auth/login → callback).
 * On 401, the auth failure callback is invoked to redirect to login.
 */
export async function fetchWithAuth(url, options = {}) {
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;

  const response = await fetch(fullUrl, {
    ...options,
    credentials: "include",
    headers: {
      ...options.headers,
    },
  });

  if (response.status === 401 && _onAuthFailure && !_authFailureFired && !_signingOut) {
    _authFailureFired = true;
    _onAuthFailure();
  }

  // A request that just queued a background job tells the activity tray to refresh
  // now, instead of waiting for its next poll — a quick job can finish before that.
  if (response.ok && JOB_START_METHODS.has(String(options.method || "GET").toUpperCase()) && JOB_START_RE.test(url)) {
    window.dispatchEvent(new Event("activity:changed"));
  }

  return response;
}

const JOB_START_METHODS = new Set(["POST", "PUT"]);
const JOB_START_RE =
  /^\/api\/(jobs\/import(\/[a-f0-9]{24}\/mapping)?|upload-pdf-candidates-page|scout\/track(\/bulk)?|find-candidates|research\/find-hr|candidates\/match-batch|jobs\/[a-f0-9]{24}\/match-candidates|user-candidates\/[^/?]+|outreach\/send|ai\/email-template|outreach\/external-candidates\/([a-f0-9]{24}\/)?find-emails?)(\?|$)/;
